import { FunctionComponent, useEffect, useState } from 'react';
import {
  BodyText,
  Button,
  Div,
  Divider,
  Flex,
  GridBag,
  Gutter,
  Icon,
  Skeleton,
  CollapsibleBlock,
  CollapsibleBlockList,
} from '@gaiads/telia-react-component-library';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import pick from 'lodash/pick';
import { DateTime } from 'luxon';
import { DATE_FORMAT, OrderStatusIndicator, OrderStatusTranslations, mapOrderStateToUi } from '@cdaas/reusable-components-daas';
import { ProductOrder } from '@cdaas/api-clients/dist/order-openapi';
import { ApprovalChainStepApprovalRequestDecisionEnum, OrganizationAssetResponse } from '@cdaas/api-clients/dist/asset-user-openapi';
import { LocalBundleEnvelope, LocalProductVariant, LocalProduct } from '@cdaas/api-clients/dist/product-service-openapi';

import { showGrowlNotification } from '../../store/actions';
import { getAllAssetsAndOngoingOrdersForAUser, getBundleById } from '../../utils';
import { IconWithBackgroundColor } from '../../Pages/Approval/ApprovalHelperComponents/IconWithBackgroundColor';
import { ContactOrDeliveryInformation } from '../../Pages/Approval/ApprovalHelperComponents/ContactOrDeliveryInformation';
import ApprovalStatusIndicator from '../ApprovalStatusIndicator/ApprovalStatusIndicator';
import { AddonProduct } from './AddonProduct';

interface OrderDetailsProps {
  order: ProductOrder;
  showBottomButtons: boolean;
  openApproveOrRejectOrderModal?: (orderRow: ProductOrder[], mode: ApprovalChainStepApprovalRequestDecisionEnum, destructive: boolean) => void;
  modalType?: 'approval' | 'order';
}

const OrderDetails: FunctionComponent<OrderDetailsProps> = ({
  order,
  showBottomButtons,
  openApproveOrRejectOrderModal = () => {},
  modalType = 'order',
}) => {
  const { t } = useTranslation('approval');
  const { t: tOrders } = useTranslation('orders');

  const [bundle, setBundle] = useState<LocalBundleEnvelope | null>();
  const [assets, setAssets] = useState<OrganizationAssetResponse[]>([]);
  const [orders, setOrders] = useState<ProductOrder[]>([]);

  const dispatch = useDispatch();

  const orderStatusTranslations: OrderStatusTranslations = {
    approved: tOrders('orderStatuses.approved'),
    processingOrder: tOrders('orderStatuses.processingOrder'),
    delivered: tOrders('orderStatuses.delivered'),
    pendingApproval: tOrders('orderStatuses.pendingApproval'),
    financingDeclined: tOrders('orderStatuses.financingDeclined'),
    approvalDeclined: tOrders('orderStatuses.approvalDeclined'),
    cannotBeProcessed: tOrders('orderStatuses.cannotBeProcessed'),
    cancelled: tOrders('orderStatuses.cancelled'),
    returned: tOrders('orderStatuses.returned'),
    discarded: tOrders('orderStatuses.discarded'),
    draft: tOrders('orderStatuses.draft'),
  };

  const getAssetsAndOrders = async (userId: string): Promise<void> => {
    const response = await getAllAssetsAndOngoingOrdersForAUser(userId);
    if (response.orders?.contents.length) {
      setOrders(response.orders.contents.sort((a, b) => new Date(b.created ?? '0').getTime() - new Date(a.created ?? '0').getTime()));
    }
    if (response.assets?.contents.length) {
      setAssets(response.assets.contents);
    }
  };

  useEffect(() => {
    const getBundle = async (id: string): Promise<void> => {
      const orderedBundle = await getBundleById(id);
      if (orderedBundle) {
        setBundle(orderedBundle);
      } else {
        dispatch(showGrowlNotification(t('OrderDetails.getBundleError'), 'warning'));
      }
    };
    getBundle(order.packages[0].external_id!);
    if (modalType === 'approval' && order.packages[0].end_user.user_id) getAssetsAndOrders(order.packages[0].end_user.user_id);
  }, [order, modalType, dispatch, t]);

  const getOrderHistoryEntry = (order: ProductOrder): string => {
    const latestStatusEntry = order.history?.reduce((prev, curr) => {
      if (curr.status === order.customer_approval.status && new Date(curr.timestamp) > new Date(prev.timestamp)) {
        return curr;
      }
      return prev;
    });
    return latestStatusEntry?.status === order.customer_approval.status
      ? `${new Date(latestStatusEntry.timestamp).toLocaleString()} - ${latestStatusEntry.description}`
      : '';
  };

  const forwardToTrackingPages = (): void => {
    if (order?.delivery_updates?.parcels?.[0].tracking_url) {
      globalThis.open(order.delivery_updates.parcels[0].tracking_url);
    }
  };

  const getImei = (asset: OrganizationAssetResponse): string => {
    if (asset.serial_number.type === 'imei') {
      return ` - ${asset.serial_number.number}`;
    } else {
      return '';
    }
  };

  let mainItemVariant: LocalProductVariant | undefined;
  if (bundle) {
    mainItemVariant = bundle.bundle.main_item.variants?.find(
      variant => variant.id.toString() === order.packages[0].order_lines.find(line => line.is_main_product)?.external_id,
    );
  }

  const prices = order.packages?.[0]?.prices?.[0];
  const leasingPeriod = order.packages?.[0]?.leasing_period;

  return (
    <>
      <Flex spaceBetween>
        <BodyText color={'gray500'}>{`${t('OrderDetails.forWho')} ${order.ordered_to.customer_name}`}</BodyText>
        {modalType === 'approval' ? (
          <ApprovalStatusIndicator status={order.customer_approval.status} textColor="gray500" />
        ) : (
          <OrderStatusIndicator status={order.status} textColor="gray500" translations={orderStatusTranslations} />
        )}
      </Flex>
      <Gutter />

      {/* Bundle information */}
      {bundle ? (
        <>
          <Div
            backgroundColor={'gray100'}
            borderRadius={{ top: true, size: 'md' }}
            padding={{ top: 'sm', bottom: 'md', horizontal: 'md' }}
            style={{ border: '1px solid var(--gaia-color-gray-200)' }}
          >
            <BodyText color={'gray500'} size="xs">
              {t('OrderDetails.mainProduct')}
            </BodyText>

            <Gutter />
            <Flex spaceBetween>
              <BodyText>{bundle.bundle.main_item.name}</BodyText>
              <BodyText color={'gray500'}>{mainItemVariant?.sku}</BodyText>
            </Flex>
          </Div>
          <Divider color="gray200" />
          <Div
            backgroundColor={'gray100'}
            borderRadius={{ bottom: true, size: 'md' }}
            padding={{ size: 'md' }}
            style={{ border: '1px solid var(--gaia-color-gray-200)' }}
          >
            <BodyText size={'xs'} color={'gray500'}>
              {t('OrderDetails.addonProducts')}
            </BodyText>
            <Gutter />

            {bundle.addon_products?.map((addon: LocalProduct, index: number) => (
              <AddonProduct key={`addon-product-${addon.id}`} addon={addon} index={index} bundle={bundle} />
            ))}
          </Div>
        </>
      ) : (
        <Div style={{ width: '999px', maxWidth: '100%', height: '850px' }}>
          <Skeleton widthInPercentages={100} heightInPercentages={100} borderRadius={{ size: 'md' }}></Skeleton>
        </Div>
      )}

      <Gutter size="xlg" />

      {/* Order information */}
      <Div margin={{ top: 'sm' }}>
        <BodyText size={'xs'} color={'gray500'}>
          {t('OrderDetails.orderId')}
        </BodyText>
        <BodyText>{order.unique_id}</BodyText>

        <Gutter />

        <BodyText size={'xs'} color={'gray500'}>
          {t('OrderDetails.orderTime')}
        </BodyText>
        <BodyText>{order.created ? DateTime.fromISO(order.created).toFormat(DATE_FORMAT) : ''}</BodyText>

        <Gutter />

        <BodyText size={'xs'} color={'gray500'}>
          {t('OrderDetails.orderStatus')}
        </BodyText>
        {modalType === 'approval' ? (
          <ApprovalStatusIndicator status={order.customer_approval.status} />
        ) : (
          <OrderStatusIndicator status={order.status} translations={orderStatusTranslations} />
        )}

        {(order.customer_approval.status === 'APPROVED' || order.customer_approval.status === 'REJECTED') && (
          <BodyText size={'sm'} color={'gray500'}>
            {getOrderHistoryEntry(order)}
          </BodyText>
        )}

        <Gutter />

        {prices?.price && (
          <>
            <BodyText size={'xs'} color={'gray500'}>
              {t('OrderDetails.price')}
            </BodyText>
            <BodyText>
              {prices.price.total} {prices.price.currency}
            </BodyText>
            <BodyText color={'gray500'} size={'sm'}>
              {t('OrderDetails.vatIncluded')}, {leasingPeriod} {t('OrderDetails.paymentPeriod')}
            </BodyText>
            <BodyText>
              {prices.price.amount} {prices.price.currency}
            </BodyText>
            <BodyText color={'gray500'} size={'sm'}>
              {t('OrderDetails.vatExcluded')}
            </BodyText>

            <Gutter />
          </>
        )}

        <BodyText size={'xs'} color={'gray500'}>
          {t('OrderDetails.deliveryTracking')}
        </BodyText>
        {order.delivery_updates?.parcels?.[0]?.tracking_url ? (
          <Flex>
            <BodyText fontWeight="medium" color="purple500" clickableAppearance onClick={forwardToTrackingPages}>
              {order.delivery_updates?.parcels?.[0]?.number}
            </BodyText>
            <Icon color={'purple500'} margin={{ left: 'xs' }} size={'sm'} name={'external'}></Icon>
          </Flex>
        ) : (
          <BodyText>{t('OrderDetails.trackingNotAvailable')}</BodyText>
        )}
      </Div>
      <Gutter size={'xlg'} />

      {/* Delivery and billing information */}
      <Div>
        <GridBag>
          <GridBag.Item sm={12} shown={order.created_by.user_id !== order.packages[0].end_user.user_id}>
            {IconWithBackgroundColor('user-admin')}
            <Gutter size={'xs'} />
            {ContactOrDeliveryInformation({
              customerData: {
                person: pick(order.created_by, ['type', 'given_name', 'family_name', 'email', 'phone_number']),
              },
              groupTitle: t('OrderDetails.purchaser'),
              adminType: t('OrderDetails.adminType'),
              contactOrDelivery: 'contact',
            })}
            <Gutter />
          </GridBag.Item>
        </GridBag>
        <Div>
          {IconWithBackgroundColor('care')}
          <Gutter size={'xs'} />
          {ContactOrDeliveryInformation({
            customerData: {
              person: pick(order.packages[0].end_user, ['given_name', 'family_name', 'email', 'phone_number']),
            },
            groupTitle: t('OrderDetails.employee'),
            contactOrDelivery: 'contact',
          })}
        </Div>
        <Gutter size="layout-5" shown={modalType === 'approval'} />
        <CollapsibleBlockList shown={modalType === 'approval'}>
          <CollapsibleBlock blockTitle={t('OrderDetails.assetList')}>
            <CollapsibleBlock.Content>
              {assets.map(asset => (
                <BodyText size="sm" key={asset.id} margin={{ bottom: 'xs' }} padding={{ bottom: 'xxs' }}>
                  {`${asset.product.name}${getImei(asset)} - ${t('OrderDetails.assetEndDate')}${' '}
                  ${DateTime.fromISO(asset.prices.lease?.end_date_planned ?? '').toFormat(DATE_FORMAT)}`}
                </BodyText>
              ))}
            </CollapsibleBlock.Content>
          </CollapsibleBlock>
          <CollapsibleBlock blockTitle={t('OrderDetails.orderHistoryList')}>
            <CollapsibleBlock.Content>
              {/* Do not show order that is in approval/rejection at the moment */}
              {orders
                .filter(listOrder => listOrder.unique_id !== order.unique_id)
                .map(order => (
                  <BodyText size="sm" key={order.unique_id} margin={{ bottom: 'xs' }} padding={{ bottom: 'xxs' }}>
                    {`${DateTime.fromISO(order.created || '').toFormat(DATE_FORMAT)} - ${order.packages[0].name}`}
                    {` - ${mapOrderStateToUi(order.status, orderStatusTranslations)}`}
                  </BodyText>
                ))}
            </CollapsibleBlock.Content>
          </CollapsibleBlock>
        </CollapsibleBlockList>

        <Gutter size={modalType === 'approval' ? 'layout-7' : 'md'} />
        <Div>
          {IconWithBackgroundColor('offering')}
          <Gutter size={'xs'} />
          {ContactOrDeliveryInformation({
            customerData: {
              person: pick(order.ship_to?.contact_person ?? order.ordered_to.contact_person, [
                'given_name',
                'family_name',
                'email',
                'phone_number',
                'country_calling_code',
              ]),
              address: order.ship_to?.address ?? order.ordered_to.address,
            },
            groupTitle: t('OrderDetails.delivery'),
            contactOrDelivery: 'delivery',
          })}
        </Div>
        <Gutter size={'sm'} />
      </Div>

      <Gutter size={'xlg'} />

      {showBottomButtons && (
        <Flex alignRight>
          <Button
            onClick={() => openApproveOrRejectOrderModal([order], ApprovalChainStepApprovalRequestDecisionEnum.Rejected, true)}
            variant={'destructive'}
          >
            {t('reject')}
          </Button>
          <Gutter size={'sm'} />
          <Button
            onClick={() => openApproveOrRejectOrderModal([order], ApprovalChainStepApprovalRequestDecisionEnum.Approved, false)}
            variant={'primary'}
          >
            {t('approve')}
          </Button>
        </Flex>
      )}
    </>
  );
};

export default OrderDetails;
